import KanbanBoard from 'components/Kanban/KanbanBoard'
import React, { useState, useEffect } from 'react'
import style from './Tasks.module.css'
import styleTable from 'assets/css/Table.module.css'
import Filter from 'components/Filter'
import { Id, Platform, Priority, Progress, Task } from 'types'
import axiosClient from 'axios-client'
import { useStateContext } from 'context/ContextProvider'
import AddButton from 'components/Buttons/Add/AddButton'
import { DatePicker, Table, Tag } from 'antd'
import type { DatePickerProps, TableColumnsType } from 'antd'
import { Link } from 'react-router-dom'
import TableOptions from 'components/Table/TableOptions'
import { updateObject } from 'utils/updateObjectList'

// Get enums
const platforms = Object.values(Platform)
const progress = Object.values(Progress)
const priority = Object.values(Priority)

const columns: TableColumnsType<Task> = [
  {
    title: 'Task title',
    dataIndex: 'title',
    key: 'title',
    render: (text, { id }) => (
      <Link
        className={style.title}
        to={'/tasks/view/' + id}
      >
        {text}
      </Link>
    ),
  },
  {
    title: 'Task id',
    dataIndex: 'task_id',
    key: 'task_id',
  },
  {
    title: 'Tags',
    key: 'tags',
    dataIndex: 'tags',
    render: (_, { tags }) => (
      <>
        <div className={styleTable.tagsCol}>
          {tags?.map(tag => {
            return (
              <Tag
                color="magenta"
                key={tag}
              >
                {tag}
              </Tag>
            )
          })}
        </div>
      </>
    ),
  },
  {
    title: 'Status',
    dataIndex: 'progress',
    key: 'progress',
    render: text => (
      <Tag
        color="magenta"
        key={text}
      >
        {text.toUpperCase()}
      </Tag>
    ),
  },
  {
    title: 'Due date',
    dataIndex: 'duedate',
    key: 'duedate',
  },
  {
    title: 'Priority',
    dataIndex: 'priority',
    key: 'priority',
    render: priority => {
      let icon = ''

      switch (priority) {
        case Priority.High:
          icon = styleTable.high
          break
        case Priority.Low:
          icon = styleTable.low
          break
        case Priority.Late:
          icon = styleTable.late
          break
        case Priority.Critical:
          icon = styleTable.critical
          break
        case Priority.Medium:
          icon = styleTable.medium
          break
      }
      return <span className={`${styleTable.prio} ${icon}`}>{priority}</span>
    },
  },
  {
    title: 'Client',
    dataIndex: ['taskable', 'name'],
    key: 'taskable',
  },
  {
    title: 'Assignee',
    dataIndex: ['assignable', 'name'],
    key: 'assignable',
  },
]

const Tasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([])
  const [showList, setShowList] = useState(false)
  const [loading, setLoading] = useState(false)
  const [dueDateFilter, setDueDateFilter] = useState('')

  const [selectedTasks, setSelectedTasks] = useState<Id[]>([])

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: Task[]) => {
      //console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
      setSelectedTasks(selectedRowKeys as Id[])
    },
  }

  // Datalist suggestions
  const suggestions: (Platform | Progress | Priority)[] = [...progress, ...platforms, ...priority]

  useEffect(() => {
    getTasks([])
  }, [])

  const getTasks = (filters: string[]) => {
    setLoading(true)

    axiosClient
      .get('/tasks', {
        params: {
          filters: filters,
        },
      })
      .then(({ data }) => {
        setTasks(data)
        setLoading(false)
      })
  }

  const handleDateChange: DatePickerProps['onChange'] = (date, dateString) => {
    console.log(date, dateString)
    setDueDateFilter(dateString as string)
  }

  const updateTaskField = (field: string, newStatus: string) => {
    // Find the account by its ID
    const updatedAccounts = updateObject<Task>({
      objects: tasks,
      selectedIds: selectedTasks,
      updateProperty: field,
      updateValue: newStatus,
    })

    // Update the state with the modified accounts array
    setTasks(updatedAccounts)
  }

  return (
    <>
      <div className={styleTable.page_title}>
        <div className={styleTable.label}>Tasks</div>
        <div className={styleTable.count}>{tasks?.length} tasks</div>
        <div className={`${style.tabs} ${showList ? style.activeList : style.activeKan}`}>
          <div
            className={`${style.tab}`}
            onClick={() => setShowList(false)}
          >
            Kanban
          </div>
          <div
            className={`${style.tab}`}
            onClick={() => setShowList(true)}
          >
            List
          </div>
        </div>
        {tasks && tasks.length > 0 && (
          <div style={{ marginLeft: 'auto' }}>
            <AddButton text="Task" />
          </div>
        )}
      </div>

      <Filter
        setFilters={getTasks}
        suggestions={suggestions.sort()}
      />

      <DatePicker onChange={handleDateChange} />

      {loading && <p>Loading...</p>}
      {!loading && (
        <>
          {!showList && tasks && (
            <div className={`${style.kanban}`}>
              <KanbanBoard tasks={tasks} />
            </div>
          )}
          {showList && (
            <>
              <div>
                <Table
                  columns={columns}
                  dataSource={tasks}
                  rowSelection={{
                    type: 'checkbox',
                    fixed: true,
                    ...rowSelection,
                  }}
                  pagination={{
                    position: ['bottomCenter'],
                    pageSizeOptions: [10, 20, 50],
                    showSizeChanger: true,
                  }}
                  locale={{
                    emptyText: (
                      <div className={style.no_record}>
                        <div className={style.text_wrapper}>Vizualize your work with a list</div>
                        <AddButton text="Task" />
                      </div>
                    ),
                  }}
                  //loading={true}
                  rowKey={record => record.id}
                  className={`${styleTable.antTable}`}
                  rowClassName={`${styleTable.antRow}`}
                  bordered
                />

                <TableOptions
                  selectedRows={selectedTasks}
                  type="tasks"
                  updateObject={updateTaskField}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}

export default Tasks
